import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import { ButtonPreloader, ButtonMiniPreloader } from 'components/shared/preloader';
import styles from './styles.module.scss';

export interface IButtonProps
  extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  className?: string;
  testID?: string;
  id?: string;
  type?: 'button' | 'submit' | 'reset';
  loaderType?: 'default' | 'mini';
  disableButtonAutoPreloader?: boolean;
  showButtonPreloader?: boolean;
  children?: React.ReactNode | string;
  errorComponent?: React.ReactElement;
  disabled?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      children,
      className,
      testID,
      id,
      type = 'button',
      disableButtonAutoPreloader,
      showButtonPreloader,
      errorComponent,
      disabled,
      loaderType = 'default',
      onClick,
      ...props
    }: IButtonProps,
    ref,
  ) => {
    const { i18n } = useTranslation();

    const [{ processing, isError }, setProcessing] = useState({ isError: false, processing: false });

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const handler = onClick?.(e);

      if (disableButtonAutoPreloader) {
        return;
      }

      if (handler && (handler as { then?: unknown }).then === undefined) {
        return;
      }

      try {
        setProcessing({ processing: true, isError: false });
        await handler;
        setProcessing({ processing: false, isError: false });
      } catch (err) {
        setProcessing({ processing: false, isError: true });
      }
    };

    const isText = (children: React.ReactNode | string): children is string => {
      return typeof children === 'string';
    };

    const error = isError ? errorComponent : null;

    const buttonPreloader =
      (!disableButtonAutoPreloader && processing) || showButtonPreloader ? (
        <div className={styles.preloaderContainer}>
          {loaderType === 'default' ? <ButtonPreloader /> : <ButtonMiniPreloader />}
        </div>
      ) : null;

    return (
      <button
        ref={ref}
        data-test-id={testID}
        id={id}
        type={type}
        className={cn(styles.button, className)}
        onClick={handleClick}
        disabled={disabled || processing || showButtonPreloader}
        {...props}>
        {error || buttonPreloader || (isText(children) ? i18n.t(children as KeyMapKeys) : children)}
      </button>
    );
  },
);

export { Button };
